import { useCallback, useEffect, useState } from 'react';

import { ProfileIcon, UserQuestion01Icon } from '@hugeicons/core-free-icons';
import moment from 'moment';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Input, InputGroup, MultipleSelect, ToggleSwitchInput } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';

export const CandidateQuestionnaireSelectorDetails = ({
  index,
  append,
  remove,
  control,
  watch,
  credits = 0,
  candidateForms,
  register,
  setValue = () => {},
  setError = () => {},
  clearErrors = () => {},
  errors = {},
}) => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(false);
  const formDetailsError = isNaN(index)
    ? null
    : errors?.checks?.[index]?.details?.form_details?.message;

  const addFormIds = formIds => {
    const previous = watch(`checks.${index}.details.form_details`) || [];
    const byId = new Map(previous.map(detail => [detail.id, detail]));
    const next = formIds.map(formId => {
      const existing = byId.get(formId);
      return existing ? { ...existing, id: formId } : { id: formId };
    });
    setValue(`checks.${index}.details.form_details`, next);
  };

  const getFormName = useCallback(
    formId => {
      return candidateForms.find(form => form.id === formId)?.name || '';
    },
    [candidateForms],
  );

  const formDetailsValue = useWatch({
    control,
    name: `checks.${index}.details.form_details`,
    defaultValue: [],
  });

  // Sets/clears a manual error for this array-field path. `index` isn't stable — it starts
  // undefined (before this check is appended to the `checks` field array) and can shift if a
  // check earlier in the list is toggled off, so the path we set an error on can change between
  // effect runs. Returning a cleanup that clears the exact path just used (rather than a fresh
  // clearErrors call keyed on whatever `index` happens to be *next* time) guarantees a
  // stale/orphaned error can never survive an index change, a disable, or an unmount — which
  // otherwise permanently blocks submitting *any* check for the rest of the session (CMP-1592,
  // CMP-1593, CMP-1595).
  useEffect(() => {
    if (!enabled || isNaN(index) || index < 0) {
      return;
    }

    const path = `checks.${index}.details.form_details`;

    if (!formDetailsValue || formDetailsValue.length < 1) {
      setError(path, {
        type: 'manual',
        message: t(
          'newCheck.components.checksSelector.candidateQuestionnaireSelectorDetails.formRequired',
        ),
      });
    } else {
      clearErrors(path);
    }

    return () => clearErrors(path);
  }, [formDetailsValue, enabled, index, setError, clearErrors, t]);

  useEffect(() => {
    if (enabled && isNaN(index)) {
      append({
        type: 'candidate_questionnaire',
        details: {},
      });
    } else if (!enabled && index >= 0) {
      remove(index);
    }
  }, [enabled]);

  useEffect(() => {
    if (index >= 0 && !enabled) setEnabled(true);
    else if (isNaN(index) && enabled) setEnabled(false);
  }, [index]);

  const formIds =
    watch(`checks.${index}.details.form_details`)?.map(formDetail => formDetail.id) || [];

  return (
    <ToggleSwitchInput
      text="Candidate Questionnaire"
      logo="https://cdn.checkmate.tech/checkmate-files/assets/checkmate_check_logo.svg"
      className={credits === 0 ? 'u-border-error u-disabled' : ''}
      subtext={(credits < 0 ? 'Unlimited' : credits) + ' remaining'}
      onChange={e => setEnabled(e.target.checked)}
      dataTestId="toggle-switch-questionnaire"
      icon={UserQuestion01Icon}
      value={enabled}>
      {enabled && index >= 0 && (
        <>
          <InputGroup title="Forms">
            <MultipleSelect
              value={formIds}
              onChange={addFormIds}
              placeholder="Select..."
              error={formDetailsError}>
              {candidateForms.map(form => (
                <MultipleSelect.Item
                  key={form.id}
                  value={form.id}
                  description={`Updated ${moment(form?.updated_at || form?.created_at).fromNow()}`}>
                  {form.name}
                </MultipleSelect.Item>
              ))}
            </MultipleSelect>
          </InputGroup>
          {formDetailsValue?.map((formDetail, i) => (
            <div
              key={i}
              className="card card-with-border u-width-100 u-border-radius u-margin-top u-padding surface-secondary u-padding-bottom--0">
              <div className="d-flex align-items-center u-margin-bottom">
                <IconComponent icon={ProfileIcon} />
                <p className="u-padding-left--small u-margin-0">{getFormName(formDetail.id)}</p>
              </div>
              <ToggleSwitchInput
                text="Add message"
                toggle
                onChange={e =>
                  setValue(
                    `checks.${index}.details.form_details.${i}.use_message`,
                    e.target.checked,
                  )
                }
                dataTestId="toggle-switch-ref-check-plus"
                value={watch(`checks.${index}.details.form_details.${i}.use_message`)}>
                <Input
                  name={`checks.${index}.details.form_details.${i}.message`}
                  register={register}
                  placeholder="Add message"
                />
              </ToggleSwitchInput>
            </div>
          ))}
        </>
      )}
    </ToggleSwitchInput>
  );
};
